import _directionDownLine from "@zenlayer/zen-design-icons/assets/direction/DownLine.svg";
import _suggestCloseCircleLine from "@zenlayer/zen-design-icons/assets/suggest/CloseCircleLine.svg";
import { useControllableValue } from 'ahooks';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import dayjs, { getDefaultFormat } from '../_util/day';
import { pickDomAttrs } from '../_util/utils';
import Chips from '../chips';
import Dropdown from '../dropdown';
import { useConfig } from '../hooks/useConfig';
import Icon from '../icon';
import Space from '../space';
import Tag from '../tag';
import CustomDateContent from './customDateContent';
import { CUSTOMKEY } from './interface';
import { calculateTimeDifference, checkAllowableTimeSpan, defaultPresets, getDefaultValue, getOtherActions, getTimezoneOffset, presetMap } from './utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const PresetDatePicker = props => {
  var _presetMap$find2;
  const {
    presets = defaultPresets,
    placeholder,
    type = 'outline',
    size = 'm',
    value,
    onChange,
    disabled,
    allowClear,
    width,
    earliestTime,
    latestTime,
    fullMinute,
    allowableTimeSpan,
    placement = 'bottomRight',
    getPopupContainer,
    prefixClass = 'zen-design',
    //TODO 写provider时统一改掉
    className,
    style,
    hiddenTimeZoneSwitch,
    ...restProps
  } = props;
  const ref = React.useRef(null);
  const baseClassName = "".concat(prefixClass, "-presetDatePicker");
  const attrs = pickDomAttrs(restProps);
  const {
    locale: {
      PresetDatePicker: PresetDatePickerLocale
    }
  } = useConfig();
  const [timeZone, _onTimeZoneChange] = useControllableValue(props, {
    defaultValue: dayjs.tz.guess(),
    defaultValuePropName: 'defaultTimeZone',
    valuePropName: 'timeZone',
    trigger: 'onTimeZoneChange'
  });
  const onTimeZoneChange = timeZone => {
    _onTimeZoneChange(timeZone);
    if (!value) return;
    const newValue = getDefaultValue(value, {
      timeZone,
      latestTime,
      earliestTime,
      fullMinute
    });
    onChange === null || onChange === void 0 || onChange(newValue);
  };
  const [open, setOpen] = useState(false);
  const [openMenuKeys, setOpenMenuKeys] = useState([]);
  const prefixIcon = useMemo(() => {
    let str = timeZone ? getTimezoneOffset(timeZone) : '';
    if (value) {
      var _presetMap$find$getSh, _presetMap$find;
      const timeShortLabel = (_presetMap$find$getSh = (_presetMap$find = presetMap.find(p => p.value === (value === null || value === void 0 ? void 0 : value.datePreset))) === null || _presetMap$find === void 0 || (_presetMap$find = _presetMap$find.getShortLabel(timeZone)) === null || _presetMap$find === void 0 ? void 0 : _presetMap$find.shortLabel) !== null && _presetMap$find$getSh !== void 0 ? _presetMap$find$getSh : calculateTimeDifference(value.startTime, value.endTime);
      if (timeShortLabel) {
        str += " | ".concat(timeShortLabel);
      }
    }
    const icon = str ? /*#__PURE__*/_jsx(Tag, {
      size: "s",
      className: "prefixTag",
      block: true,
      width: 90,
      children: str
    }) : undefined;
    return icon;
  }, [timeZone, value]);
  const suffixIcon = useMemo(() => {
    const allowClearIcon = allowClear && value !== undefined ? /*#__PURE__*/_jsx(Icon, {
      type: "suggest/CloseCircleLine",
      block: true,
      onClick: e => {
        e.stopPropagation();
        onChange === null || onChange === void 0 || onChange(undefined);
      },
      style: {
        zIndex: 2,
        cursor: 'pointer'
      }
    }) : /*#__PURE__*/_jsx(Icon, {
      type: "direction/DownLine",
      block: true,
      fontSize: 16
    });
    return allowClearIcon;
  }, [allowClear, value]);
  const menuItems = useMemo(() => {
    let items = [];
    const presetItems = presets.map(i => {
      const info = presetMap.find(p => p.value === i);
      const value = getDefaultValue({
        datePreset: info === null || info === void 0 ? void 0 : info.value
      }, {
        timeZone,
        latestTime,
        earliestTime,
        fullMinute
      });
      const check = dayjs(value.startTime).isBefore(dayjs(value.endTime)) && checkAllowableTimeSpan({
        ...value,
        allowableTimeSpan
      });
      const shortLabel = info === null || info === void 0 ? void 0 : info.getShortLabel(timeZone);
      return check ? {
        key: info === null || info === void 0 ? void 0 : info.value,
        label: /*#__PURE__*/_jsxs(Space, {
          children: [/*#__PURE__*/_jsx(Tag, {
            width: 40,
            type: shortLabel === null || shortLabel === void 0 ? void 0 : shortLabel.type,
            children: shortLabel === null || shortLabel === void 0 ? void 0 : shortLabel.shortLabel
          }), /*#__PURE__*/_jsx("div", {
            children: info === null || info === void 0 ? void 0 : info.label
          })]
        }),
        onClick: () => {
          onChange === null || onChange === void 0 || onChange(value);
          setOpen(false);
          setOpenMenuKeys([]);
        }
      } : undefined;
    }).filter(i => !!i);
    if (!!presetItems.length) {
      items.push({
        key: 'presets',
        type: 'group',
        className: 'presetsGroup',
        children: presetItems
      });
    }
    const otherActives = getOtherActions({
      timeZone,
      latestTime,
      earliestTime,
      fullMinute,
      allowableTimeSpan
    });
    if (!!otherActives.length) {
      items.push({
        key: 'otherGroup',
        label: PresetDatePickerLocale.label_otherActions,
        type: 'group',
        className: 'otherGroup',
        children: [{
          key: 'otherActions',
          className: 'otherActions',
          label: /*#__PURE__*/_jsx(Space, {
            children: otherActives.map(i => /*#__PURE__*/_jsx(Tag, {
              className: "otherAction",
              onClick: () => {
                onChange === null || onChange === void 0 || onChange(i.value);
              },
              children: i.label
            }, i.label))
          })
        }]
      });
    }
    if (!!(items !== null && items !== void 0 && items.length)) {
      items.push({
        type: 'divider'
      });
    }
    items.push({
      key: CUSTOMKEY,
      label: PresetDatePickerLocale.label_custom,
      popupClassName: 'customDate-dropdown',
      onTitleClick(info) {
        setOpenMenuKeys([info.key]);
      },
      children: [{
        key: 'customChildren',
        className: 'customDate-dropdown-item',
        disabled: true,
        label: /*#__PURE__*/_jsx(CustomDateContent, {
          value: value,
          timeZone: timeZone,
          earliestTime: earliestTime,
          latestTime: latestTime,
          allowableTimeSpan: allowableTimeSpan,
          onApply: value => {
            onChange === null || onChange === void 0 || onChange(value);
            setOpen(false);
            setOpenMenuKeys([]);
          }
        })
      }]
    });
    if (!hiddenTimeZoneSwitch) {
      var _timeZoneItems$find;
      const timeZoneItems = [{
        key: 'UTC',
        label: 'UTC+0',
        onClick: info => {
          onTimeZoneChange(info.key);
          setOpen(false);
          setOpenMenuKeys([]);
        }
      }, {
        key: dayjs.tz.guess(),
        label: "".concat(getTimezoneOffset(dayjs.tz.guess()), " (").concat(PresetDatePickerLocale.label_localTime, ")"),
        onClick: info => {
          onTimeZoneChange(info.key);
          setOpen(false);
          setOpenMenuKeys([]);
        }
      }];
      items.push({
        key: 'timeZone',
        label: /*#__PURE__*/_jsxs("div", {
          className: "timeZoneLabel",
          children: [/*#__PURE__*/_jsx("div", {
            children: PresetDatePickerLocale.label_timeZone
          }), /*#__PURE__*/_jsx("div", {
            children: (_timeZoneItems$find = timeZoneItems.find(i => (i === null || i === void 0 ? void 0 : i.key) === timeZone)) === null || _timeZoneItems$find === void 0 ? void 0 : _timeZoneItems$find.label
          })]
        }),
        popupClassName: 'timeZone-dropdown',
        onTitleClick(info) {
          setOpenMenuKeys([info.key]);
        },
        children: timeZoneItems
      });
    }
    return items;
  }, [presets, value, timeZone]);
  return /*#__PURE__*/_jsx(Dropdown, {
    menu: {
      openKeys: openMenuKeys,
      selectedKeys: value !== null && value !== void 0 && value.datePreset ? [value.datePreset, timeZone] : [timeZone],
      items: menuItems,
      getPopupContainer: () => {
        var _ref$current;
        return ((_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.parentElement) || document.body;
      }
    },
    destroyPopupOnHide: true,
    disabled: disabled,
    open: open,
    trigger: ['click'],
    onOpenChange: open => {
      if (open) {
        if ((value === null || value === void 0 ? void 0 : value.datePreset) === CUSTOMKEY) {
          window.setTimeout(() => {
            setOpenMenuKeys([CUSTOMKEY]);
          }, 200);
        }
      } else {
        setOpenMenuKeys([]);
      }
      setOpen(open);
    },
    placement: placement,
    overlayClassName: "".concat(baseClassName, "-dropdown"),
    getPopupContainer: getPopupContainer,
    children: /*#__PURE__*/_jsx(Chips, {
      ...attrs.data,
      type: type,
      size: size,
      disabled: disabled,
      active: open,
      prefix: prefixIcon,
      suffix: suffixIcon,
      style: style,
      width: width !== null && width !== void 0 ? width : (value === null || value === void 0 ? void 0 : value.datePreset) === CUSTOMKEY ? 470 : 320,
      noPadding: true,
      className: classNames(className, baseClassName),
      children: /*#__PURE__*/_jsx("div", {
        ref: ref,
        className: classNames("dtaeContent", {
          ["".concat(size)]: !!size,
          ["with-prefixIcon"]: !!prefixIcon,
          ["with-suffixIcon"]: !!suffixIcon
        }),
        children: value !== null && value !== void 0 && value.datePreset ? (value === null || value === void 0 ? void 0 : value.datePreset) === CUSTOMKEY ? dayjs(value.startTime).tz(timeZone).format(getDefaultFormat()) + ' - ' + dayjs(value.endTime).tz(timeZone).format(getDefaultFormat()) : (_presetMap$find2 = presetMap.find(i => i.value === value.datePreset)) === null || _presetMap$find2 === void 0 ? void 0 : _presetMap$find2.label : /*#__PURE__*/_jsx("div", {
          className: "placeholder",
          children: placeholder !== null && placeholder !== void 0 ? placeholder : PresetDatePickerLocale.placeholder
        })
      })
    })
  });
};
export default PresetDatePicker;